import { useEffect, useRef, useState, memo } from 'react';
import { createChart } from 'lightweight-charts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const BinanceChart = memo(({ onPriceClick, timeframe, setTimeframe, markers = [], readOnly = false, symbol = 'BTC-USDT' }) => {
  const chartContainerRef = useRef(null);
  const chartRef = useRef(null);
  const candlestickSeriesRef = useRef(null);
  const markerSeriesRef = useRef(null);
  const tooltipRef = useRef(null);
  const priceLineRefs = useRef([]);
  
  const [currentPrice, setCurrentPrice] = useState(null);
  const [internalMarkers, setInternalMarkers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hoverPrice, setHoverPrice] = useState(null);
  const [isInverted, setIsInverted] = useState(false);
  const [rulerLine, setRulerLine] = useState(null);
  const [priceConfirmed, setPriceConfirmed] = useState(false);
  const rulerLineSeriesRef = useRef(null);
  const lastTouchTimeRef = useRef(0);
  const lastTouchPriceRef = useRef(null);

  const timeframes = [
    { label: '1m', value: '1m', interval: 60000 },
    { label: '5m', value: '5m', interval: 300000 },
    { label: '15m', value: '15m', interval: 900000 },
    { label: '30m', value: '30m', interval: 1800000 },
    { label: '1h', value: '1h', interval: 3600000 },
    { label: '4h', value: '4h', interval: 14400000 },
    { label: '1d', value: '1d', interval: 86400000 }
  ];

  // Fetch klines data from OKX via backend proxy
  const fetchKlines = async (interval = '5m', limit = 200) => {
    try {
      console.log('Fetching klines from OKX via backend...', interval);
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(
        `${backendUrl}/api/binance/klines?symbol=${symbol}&interval=${interval}&limit=${limit}`
      );
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('Received klines data:', data.length, 'candles');
      
      if (data.length === 0) {
        console.warn('No klines data received');
        return [];
      }
      
      const formattedData = data.map(candle => ({
        time: Math.floor(candle[0] / 1000), // Convert to seconds and ensure integer
        open: parseFloat(candle[1]),
        high: parseFloat(candle[2]),
        low: parseFloat(candle[3]),
        close: parseFloat(candle[4])
      }));
      
      console.log('Formatted data sample:', formattedData[0], formattedData[formattedData.length - 1]);
      return formattedData;
    } catch (error) {
      console.error('Error fetching klines:', error);
      toast.error('Ошибка загрузки данных с OKX');
      return [];
    }
  };

  // Fetch current price via backend proxy
  const fetchCurrentPrice = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${backendUrl}/api/binance/price?symbol=BTC-USDT`);
      const data = await response.json();
      if (data.price) {
        setCurrentPrice(parseFloat(data.price));
      }
    } catch (error) {
      console.error('Error fetching current price:', error);
    }
  };

  // Initialize chart - only once
  useEffect(() => {
    if (!chartContainerRef.current) return;
    if (chartRef.current) return; // Already initialized

    console.log('Initializing chart...');

    const chart = createChart(chartContainerRef.current, {
      width: chartContainerRef.current.clientWidth,
      height: 500,
      layout: {
        backgroundColor: '#0f172a',
        textColor: '#94a3b8',
      },
      grid: {
        vertLines: { color: '#1e293b' },
        horzLines: { color: '#1e293b' },
      },
      crosshair: {
        mode: 1,
      },
      rightPriceScale: {
        borderColor: '#334155',
      },
      timeScale: {
        borderColor: '#334155',
        timeVisible: true,
        secondsVisible: false,
      },
      localization: {
        locale: 'en-US',
      },
    });

    const candlestickSeries = chart.addCandlestickSeries({
      upColor: '#10b981',
      downColor: '#ef4444',
      borderVisible: false,
      wickUpColor: '#10b981',
      wickDownColor: '#ef4444',
    });

    chartRef.current = chart;
    candlestickSeriesRef.current = candlestickSeries;

    console.log('Chart initialized successfully');

    // Handle clicks on chart with better mobile support
    const clickHandler = (param) => {
      // Skip clicks if readOnly mode
      if (readOnly) return;
      
      // Allow clicks anywhere on the chart, not just on candles
      if (param.point) {
        const now = Date.now();
        const price = candlestickSeries.coordinateToPrice(param.point.y);
        
        if (price && onPriceClick) {
          // Debounce for mobile - prevent duplicate touches
          const timeSinceLastTouch = now - lastTouchTimeRef.current;
          const isSamePrice = lastTouchPriceRef.current && Math.abs(price - lastTouchPriceRef.current) < 1;
          
          if (timeSinceLastTouch > 500 || !isSamePrice) {
            console.log('Chart clicked, price:', price);
            onPriceClick(Math.round(price * 100) / 100);
            lastTouchTimeRef.current = now;
            lastTouchPriceRef.current = price;
          } else {
            console.log('Duplicate touch ignored');
          }
        }
      }
    };
    
    chart.subscribeClick(clickHandler);

    // Handle crosshair move to show price
    const crosshairMoveHandler = (param) => {
      // Show price anywhere on chart, not just over candles
      if (!param.point) {
        setHoverPrice(null);
        return;
      }
      
      const price = candlestickSeries.coordinateToPrice(param.point.y);
      if (price) {
        setHoverPrice(Math.round(price * 100) / 100);
      }
    };
    
    chart.subscribeCrosshairMove(crosshairMoveHandler);

    // Handle resize
    const handleResize = () => {
      if (chartContainerRef.current && chartRef.current) {
        chartRef.current.applyOptions({
          width: chartContainerRef.current.clientWidth,
        });
      }
    };

    window.addEventListener('resize', handleResize);

    return () => {
      console.log('Cleaning up chart');
      window.removeEventListener('resize', handleResize);
      if (chartRef.current) {
        chart.unsubscribeClick(clickHandler);
        chart.unsubscribeCrosshairMove(crosshairMoveHandler);
        chartRef.current.remove();
        chartRef.current = null;
        candlestickSeriesRef.current = null;
      }
    };
  }, []); // Empty deps - only run once

  // Toggle chart inversion
  const toggleInvert = () => {
    if (chartRef.current) {
      const priceScale = chartRef.current.priceScale('right');
      setIsInverted(!isInverted);
      priceScale.applyOptions({
        invertScale: !isInverted
      });
      toast.success(isInverted ? 'График нормальный' : 'График перевернут');
    }
  };

  // Add ruler line at current hover price
  const addRulerLine = () => {
    if (!chartRef.current || !hoverPrice) {
      toast.error('Наведите курсор на график');
      return;
    }

    // Remove existing ruler line if any
    if (rulerLineSeriesRef.current) {
      chartRef.current.removeSeries(rulerLineSeriesRef.current);
    }

    // Create line series for ruler
    const lineSeries = chartRef.current.addLineSeries({
      color: '#3b82f6',
      lineWidth: 2,
      lineStyle: 2, // Dashed
      priceLineVisible: true,
      lastValueVisible: true,
      crosshairMarkerVisible: false,
    });

    // Get all time points from candlestick data
    const data = candlestickSeriesRef.current?.data();
    if (data && data.length > 0) {
      const lineData = data.map(candle => ({
        time: candle.time,
        value: hoverPrice
      }));
      lineSeries.setData(lineData);
      rulerLineSeriesRef.current = lineSeries;
      setRulerLine(hoverPrice);
      toast.success(`Линейка добавлена на ${hoverPrice}`);
    }
  };

  // Remove ruler line
  const removeRulerLine = () => {
    if (rulerLineSeriesRef.current && chartRef.current) {
      chartRef.current.removeSeries(rulerLineSeriesRef.current);
      rulerLineSeriesRef.current = null;
      setRulerLine(null);
      toast.success('Линейка удалена');
    }
  };

  // Load data when timeframe changes
  useEffect(() => {
    let isMounted = true;
    
    const loadData = async () => {
      // Wait for chart initialization
      let attempts = 0;
      while (!candlestickSeriesRef.current && attempts < 20) {
        await new Promise(resolve => setTimeout(resolve, 100));
        attempts++;
      }
      
      if (!candlestickSeriesRef.current || !chartRef.current) {
        console.warn('Chart not initialized after waiting');
        return;
      }
      
      if (!isMounted) return;
      
      setIsLoading(true);
      console.log('Loading data for timeframe:', timeframe);
      
      const klines = await fetchKlines(timeframe);
      
      if (!isMounted) return;
      
      if (candlestickSeriesRef.current && klines.length > 0) {
        console.log('Setting data to chart, count:', klines.length);
        try {
          candlestickSeriesRef.current.setData(klines);
          console.log('Data set successfully');
          chartRef.current.timeScale().fitContent();
          console.log('Chart fitted');
        } catch (error) {
          console.error('Error setting chart data:', error);
          if (isMounted) {
            toast.error('Ошибка отображения графика');
          }
        }
      }
      
      if (isMounted) {
        setIsLoading(false);
      }
    };

    loadData();
    fetchCurrentPrice();

    // Update current price every 5 seconds
    const priceInterval = setInterval(() => {
      if (isMounted) {
        fetchCurrentPrice();
      }
    }, 5000);

    return () => {
      isMounted = false;
      clearInterval(priceInterval);
    };
  }, [timeframe]);

  // Draw price markers on chart when markers prop changes
  useEffect(() => {
    if (!candlestickSeriesRef.current || !markers || markers.length === 0) return;

    // Clear existing price lines
    priceLineRefs.current.forEach(line => {
      try {
        candlestickSeriesRef.current.removePriceLine(line);
      } catch (e) {
        console.warn('Error removing price line:', e);
      }
    });
    priceLineRefs.current = [];

    // Add new price lines for each marker
    markers.forEach(marker => {
      try {
        const priceLine = candlestickSeriesRef.current.createPriceLine({
          price: marker.price,
          color: marker.color || '#00ff88',
          lineWidth: 2,
          lineStyle: marker.type === 'exit' ? 2 : 0, // Dashed for exit, solid for entry
          axisLabelVisible: true,
          title: marker.label || `$${marker.price.toFixed(2)}`,
        });
        priceLineRefs.current.push(priceLine);
      } catch (e) {
        console.warn('Error creating price line:', e);
      }
    });
  }, [markers]);

  return (
    <Card className="bg-slate-900/60 border-slate-800/50 backdrop-blur">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-white">{readOnly ? `График ${symbol}` : 'График BTC/USDT'}</CardTitle>
            {!readOnly && (
              <p className="text-sm text-slate-400 mt-1">
                Кликните на график, чтобы выбрать цену
              </p>
            )}
          </div>
          {currentPrice && (
            <div className="text-right">
              <div className="text-xs text-slate-400">Текущая цена</div>
              <div className="text-2xl font-bold text-emerald-400">
                ${currentPrice.toLocaleString()}
              </div>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {/* Timeframe selector - hidden in readOnly mode */}
        {!readOnly && setTimeframe && (
          <div className="flex flex-wrap gap-2 mb-4">
            {timeframes.map((tf) => (
              <Button
                key={tf.value}
                size="sm"
                variant={timeframe === tf.value ? 'default' : 'outline'}
                onClick={() => setTimeframe(tf.value)}
                className={
                  timeframe === tf.value
                    ? 'bg-blue-600 hover:bg-blue-700'
                    : 'border-slate-600 text-slate-300 hover:bg-slate-700'
                }
              >
                {tf.label}
              </Button>
            ))}
          </div>
        )}
        
        {/* Chart controls - hidden in readOnly mode */}
        {!readOnly && (
          <div className="flex flex-wrap gap-2 mb-4">
            <Button
              size="sm"
              variant="outline"
              onClick={toggleInvert}
              className={`border-slate-600 text-slate-300 hover:bg-slate-700 ${isInverted ? 'bg-slate-700' : ''}`}
            >
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
              </svg>
              {isInverted ? 'Вернуть' : 'Перевернуть'}
            </Button>
            
            {!rulerLine ? (
              <Button
                size="sm"
                variant="outline"
                onClick={addRulerLine}
                className="border-slate-600 text-slate-300 hover:bg-slate-700"
              >
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
                Линейка
              </Button>
            ) : (
              <Button
                size="sm"
                variant="outline"
                onClick={removeRulerLine}
                className="border-blue-600 text-blue-400 hover:bg-slate-700 bg-blue-950/30"
              >
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                Удалить линейку (${rulerLine})
              </Button>
            )}
            
            <div className="text-xs text-slate-500 flex items-center ml-auto">
              💡 На мобильном: долгое нажатие для точности
            </div>
          </div>
        )}

        {/* Chart container */}
        <div className="relative">
          {isLoading && (
            <div className="absolute inset-0 flex items-center justify-center bg-slate-900/50 z-10 rounded-lg">
              <div className="text-slate-400">Загрузка данных...</div>
            </div>
          )}
          
          {/* Hover price tooltip - only in interactive mode */}
          {!readOnly && hoverPrice && (
            <div 
              className={`absolute top-2 left-2 rounded-lg px-4 py-3 z-20 transition-all shadow-xl ${
                priceConfirmed 
                  ? 'bg-emerald-600/95 border-2 border-emerald-400 scale-105' 
                  : 'bg-slate-900/95 border-2 border-blue-500/70 lg:bg-slate-900/90 lg:border-blue-500/50'
              } lg:pointer-events-none lg:cursor-default cursor-pointer active:scale-95 hover:scale-105 lg:hover:scale-100`}
              onClick={(e) => {
                e.stopPropagation();
                if (onPriceClick && window.innerWidth < 1024) {
                  setPriceConfirmed(true);
                  onPriceClick(hoverPrice);
                  setTimeout(() => setPriceConfirmed(false), 800);
                }
              }}
              onTouchEnd={(e) => {
                e.stopPropagation();
                if (onPriceClick && window.innerWidth < 1024) {
                  setPriceConfirmed(true);
                  onPriceClick(hoverPrice);
                  setTimeout(() => setPriceConfirmed(false), 800);
                }
              }}
            >
              <div className="text-xs text-slate-400 lg:text-slate-400">Цена</div>
              <div className="text-xl md:text-2xl font-bold text-white">
                ${hoverPrice.toLocaleString()}
              </div>
              {!priceConfirmed && (
                <button 
                  className="text-xs text-white mt-2 flex items-center gap-1 lg:hidden bg-blue-600 hover:bg-blue-700 px-3 py-1.5 rounded-md font-semibold w-full justify-center"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (onPriceClick) {
                      setPriceConfirmed(true);
                      onPriceClick(hoverPrice);
                      setTimeout(() => setPriceConfirmed(false), 800);
                    }
                  }}
                >
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                  </svg>
                  Добавить точку
                </button>
              )}
              {priceConfirmed && (
                <div className="text-sm text-white mt-2 flex items-center gap-2 lg:hidden font-semibold justify-center bg-emerald-500/20 px-3 py-1.5 rounded-md">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  Добавлено!
                </div>
              )}
            </div>
          )}
          
          <div 
            ref={chartContainerRef} 
            className="rounded-lg overflow-hidden touch-manipulation"
            style={{ touchAction: 'pan-x pan-y' }}
          />
        </div>
      </CardContent>
    </Card>
  );
});

BinanceChart.displayName = 'BinanceChart';

export default BinanceChart;
